// AstrologicalAgenda, personalized monthly astrological calendar.
// Route: /agenda
// Premium feature: paid users see full scores; free users see blurred calendar.
//
// Calendar data is fetched from generate-monthly-calendar Edge Function,
// which stores results in astrological_calendar and returns fromCache on hits.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { ChevronLeft, ChevronRight, Bell, BellOff, Lock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { safeInvoke } from '../lib/safeInvoke';
import { supabase } from '../lib/supabase';
import { EmptyState } from '../components/ui/EmptyState';

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

interface DayInsight {
  title:  string;
  areas:  string[];
  energy: string;
  tip:    string;
}

interface CalendarData {
  love_days:          Record<string, number>;
  work_days:          Record<string, number>;
  health_days:        Record<string, number>;
  creativity_days:    Record<string, number>;
  best_days_love:     string[];
  best_days_work:     string[];
  best_days_health:   string[];
  avoid_days:         string[];
  day_insights:       Record<string, DayInsight>;
  retrograde_periods: Record<string, string[]>;
  new_moon_date:      string | null;
  full_moon_date:     string | null;
  eclipse_dates:      string[];
  monthly_summary:    string;
}

type AreaTab = 'all' | 'love' | 'work' | 'health' | 'creativity';

// ─────────────────────────────────────────
// Constants
// ─────────────────────────────────────────

const AREA_TABS: { key: AreaTab; label: string; emoji: string }[] = [
  { key: 'love',       label: 'Amor',        emoji: '💕' },
  { key: 'work',       label: 'Trabalho',    emoji: '💼' },
  { key: 'health',     label: 'Saúde',       emoji: '🌿' },
  { key: 'creativity', label: 'Criatividade', emoji: '✨' },
  { key: 'all',        label: 'Todos',       emoji: '📅' },
];

const PT_MONTHS = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

const PT_WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// ─────────────────────────────────────────
// Score colour helpers
// ─────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 8) return '#4A7C59'; // sage green, WCAG AA compliant on dark bg
  if (score >= 5) return 'var(--petrol)';
  return 'var(--text-muted)';
}

function scoreBg(score: number): string {
  if (score >= 8) return 'rgba(74,124,89,0.15)';
  if (score >= 5) return 'rgba(31,78,95,0.15)';
  return 'rgba(255,255,255,0.05)';
}

// ─────────────────────────────────────────
// Moon phase per day (synodic month)
// ─────────────────────────────────────────

const MOON_PHASES = [
  { emoji: '🌑', name: 'Lua Nova',             influence: 'Momento de intenções e novos começos. Plante as sementes do que deseja criar neste ciclo.' },
  { emoji: '🌒', name: 'Lua Crescente',         influence: 'Energia em expansão. Tome iniciativas, dê os primeiros passos e confie no impulso.' },
  { emoji: '🌓', name: 'Quarto Crescente',      influence: 'Momento de ação e decisão. Supere obstáculos com foco e determinação.' },
  { emoji: '🌔', name: 'Gibosa Crescente',      influence: 'Refine e ajuste seus projetos. A colheita está próxima, persevere.' },
  { emoji: '🌕', name: 'Lua Cheia',             influence: 'Ápice de energia e clareza. Celebre conquistas e libere o que não serve mais.' },
  { emoji: '🌖', name: 'Gibosa Minguante',      influence: 'Hora de agradecer e compartilhar. Distribua o que aprendeu neste ciclo.' },
  { emoji: '🌗', name: 'Quarto Minguante',      influence: 'Libere e perdoe. Deixe ir padrões e situações que não funcionam mais.' },
  { emoji: '🌘', name: 'Lua Minguante',         influence: 'Recolhimento e descanso interior. Prepare-se para o próximo ciclo.' },
] as const;

function getMoonPhase(dateStr: string): typeof MOON_PHASES[number] {
  const REF_JD  = 2451550.259;
  const SYNODIC = 29.53058867;
  const d = new Date(dateStr + 'T12:00:00');
  const y = d.getUTCFullYear(), m = d.getUTCMonth() + 1, day = d.getUTCDate();
  const a = Math.floor((14 - m) / 12);
  const yr = y + 4800 - a;
  const mo = m + 12 * a - 3;
  const jd = day + Math.floor((153 * mo + 2) / 5) + 365 * yr
    + Math.floor(yr / 4) - Math.floor(yr / 100) + Math.floor(yr / 400) - 32045;
  const pct = (((jd - REF_JD) % SYNODIC) + SYNODIC) % SYNODIC / SYNODIC;
  const idx = Math.min(7, Math.floor(pct * 8));
  return MOON_PHASES[idx];
}

// ─────────────────────────────────────────
// Moon sign (tropical zodiac, ~2.5 days per sign)
// Reference: J2000.0 moon mean longitude ≈ 218.3165° (Scorpio)
// Mean motion: 13.17639648°/day
// ─────────────────────────────────────────

const MOON_SIGNS = [
  { name: 'Áries',       symbol: '♈', element: 'Fogo',  modality: 'Cardinal', keywords: 'impulso e novos começos' },
  { name: 'Touro',       symbol: '♉', element: 'Terra', modality: 'Fixo',     keywords: 'estabilidade e prazer sensorial' },
  { name: 'Gêmeos',      symbol: '♊', element: 'Ar',    modality: 'Mutável',  keywords: 'comunicação e curiosidade' },
  { name: 'Câncer',      symbol: '♋', element: 'Água',  modality: 'Cardinal', keywords: 'emoção e intuição' },
  { name: 'Leão',        symbol: '♌', element: 'Fogo',  modality: 'Fixo',     keywords: 'criatividade e expressão' },
  { name: 'Virgem',      symbol: '♍', element: 'Terra', modality: 'Mutável',  keywords: 'análise e serviço' },
  { name: 'Libra',       symbol: '♎', element: 'Ar',    modality: 'Cardinal', keywords: 'equilíbrio e parcerias' },
  { name: 'Escorpião',   symbol: '♏', element: 'Água',  modality: 'Fixo',     keywords: 'transformação e profundidade' },
  { name: 'Sagitário',   symbol: '♐', element: 'Fogo',  modality: 'Mutável',  keywords: 'expansão e filosofia' },
  { name: 'Capricórnio', symbol: '♑', element: 'Terra', modality: 'Cardinal', keywords: 'disciplina e ambição' },
  { name: 'Aquário',     symbol: '♒', element: 'Ar',    modality: 'Fixo',     keywords: 'inovação e coletividade' },
  { name: 'Peixes',      symbol: '♓', element: 'Água',  modality: 'Mutável',  keywords: 'intuição e compaixão' },
] as const;

function getMoonSign(dateStr: string): typeof MOON_SIGNS[number] {
  const REF_MS   = Date.UTC(2000, 0, 1, 12, 0, 0); // J2000.0
  const REF_LON  = 218.3165;  // moon mean longitude at J2000.0
  const MEAN_MOT = 13.17639648; // degrees per day
  const d = new Date(dateStr + 'T12:00:00Z');
  const days = (d.getTime() - REF_MS) / 86400000;
  const lon  = ((REF_LON + MEAN_MOT * days) % 360 + 360) % 360;
  return MOON_SIGNS[Math.floor(lon / 30)];
}

// ─────────────────────────────────────────
// Per-planet retrograde impacts
// ─────────────────────────────────────────

const PLANET_IMPACTS: Record<string, string> = {
  'Mercúrio': 'Mercúrio rege comunicação, contratos, tecnologia e viagens. Evite assinar documentos, comprar eletrônicos ou tomar grandes decisões agora. Favoreça tudo que começa com "re-": revisar, reconectar, reescrever, pesquisar.',
  'Vênus':    'Reavalie relacionamentos e finanças. Não é o momento ideal para novas compras ou declarações amorosas.',
  'Marte':    'Canalize a energia com cautela. Conflitos tendem a escalar, prefira diplomacia à confrontação.',
  'Júpiter':  'Questione planos de expansão. Oportunidades podem parecer maiores do que realmente são.',
  'Saturno':  'Revise estruturas e responsabilidades. Bom para questionar compromissos de longo prazo.',
  'Urano':    'Mudanças inesperadas podem surgir. Mantenha flexibilidade e abertura ao novo.',
  'Netuno':   'Intensifica sonhos e intuição, mas também ilusões. Verifique informações antes de agir.',
  'Plutão':   'Transformações profundas em andamento. Enfrente padrões que precisam mudar.',
};

// ─────────────────────────────────────────
// Score for active area tab
// ─────────────────────────────────────────

function areaScore(data: CalendarData, dateStr: string, area: AreaTab): number {
  if (area === 'all') {
    const scores = [
      data.love_days[dateStr] ?? 5,
      data.work_days[dateStr] ?? 5,
      data.health_days[dateStr] ?? 5,
      data.creativity_days[dateStr] ?? 5,
    ];
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }
  const map: Record<AreaTab, keyof CalendarData> = {
    love:       'love_days',
    work:       'work_days',
    health:     'health_days',
    creativity: 'creativity_days',
    all:        'love_days',
  };
  return (data[map[area]] as Record<string, number>)[dateStr] ?? 5;
}

const PLANET_GLYPHS: Record<string, string> = {
  'Mercúrio': '☿', 'Vênus': '♀', 'Marte': '♂',
  'Júpiter':  '♃', 'Saturno': '♄', 'Urano': '♅',
  'Netuno':   '♆', 'Plutão': '♇',
};

// ─────────────────────────────────────────
// Moon-phase-specific retrograde impact (8 phases × planet)
// ─────────────────────────────────────────

const RETROGRADE_MOON_CONTEXT: Record<string, Partial<Record<string, string>>> = {
  'Mercúrio': {
    'Lua Nova':         'Lua Nova com Mercúrio retrógrado: plante intenções internamente, mas não as anuncie ainda. Reconecte-se com projetos antigos e velhos contatos eles podem trazer surpresas positivas. Evite assinar contratos ou adquirir eletrônicos.',
    'Lua Crescente':    'O ímpeto de avançar é grande, mas use esta energia para pesquisar, ler e revisar não para fechar acordos ou fazer grandes compras. Viagens planejadas agora tendem a ter imprevistos; confirme reservas com antecedência.',
    'Quarto Crescente': 'O impulso de decidir é intenso mas este não é o momento para assinar contratos, comprar carros, eletrônicos ou itens de valor. Revise o que já existe em vez de iniciar o que é novo.',
    'Gibosa Crescente': 'Revisar e aprimorar é exatamente o que Mercúrio retrógrado pede: edite, corrija, reescreva. Evite fechar acordos ou adquirir aparelhos adie essas ações para depois do retorno direto.',
    'Lua Cheia':        'A Lua Cheia ilumina conversas não resolvidas e contratos que merecem revisão. Ótimo para sanar mal-entendidos e recuperar mensagens perdidas péssimo para fechar novos acordos ou comprar tecnologia.',
    'Gibosa Minguante': 'Recontacte velhos amigos e ex-parceiros de negócios laços dormentes podem se tornar oportunidades reais. Evite compras impulsivas de eletrônicos, móveis ou itens caros; espere Mercúrio ficar direto.',
    'Quarto Minguante': 'Libere projetos de comunicação que precisam ser reescritos do zero. Edição, revisão e correção são as grandes forças deste momento tudo com prefixo "re" ganha força agora.',
    'Lua Minguante':    'Período balsâmico e retrógrado combinados: máximo recolhimento. Faça backup de arquivos, revise contratos passados e adie decisões importantes para quando Mercúrio estiver direto.',
  },
  'Vênus': {
    'Lua Nova':         'Reavalie o que realmente valoriza novos começos em afetos e finanças pedem maturidade.',
    'Lua Crescente':    'Impulso para novas conexões, mas revise padrões relacionais antes de avançar.',
    'Quarto Crescente': 'Decisões afetivas e financeiras pedem revisão não se deixe levar pela impulsividade.',
    'Gibosa Crescente': 'Refine vínculos existentes antes de buscar novos qualidade antes de quantidade.',
    'Lua Cheia':        'Emoções nos relacionamentos afloram expresse com gentileza, não com reatividade.',
    'Gibosa Minguante': 'Agradeça os vínculos que nutrem e avalie os que drenam sua energia vital.',
    'Quarto Minguante': 'Libere apegos e dinâmicas afetivas que já não te alimentam genuinamente.',
    'Lua Minguante':    'Introspecção relacional o que você realmente busca no amor e na parceria?',
  },
  'Marte': {
    'Lua Nova':         'Canalize a energia nascente com foco estratégico evite confrontos impulsivos.',
    'Lua Crescente':    'Ímpeto de ação em alta, mas valide a direção antes de acelerar o passo.',
    'Quarto Crescente': 'Aja com estratégia, não força bruta a sabedoria supera a impulsividade aqui.',
    'Gibosa Crescente': 'Perto da meta resista à frustração e mantenha o esforço consciente e constante.',
    'Lua Cheia':        'A Lua Cheia pode inflamar conflitos respire fundo antes de qualquer reação.',
    'Gibosa Minguante': 'Compartilhe conquistas e colabore generosidade gera mais força que competição.',
    'Quarto Minguante': 'Libere raiva e frustração de forma saudável movimento físico é remédio eficaz.',
    'Lua Minguante':    'Restaure a energia vital descanso ativo é mais produtivo que esforço forçado.',
  },
  'Júpiter': {
    'Lua Nova':         'Oportunidades que surgem podem parecer maiores do que são avalie com calma e realismo.',
    'Lua Cheia':        'A abundância da Lua Cheia pode inflar expectativas seja realista sobre o possível.',
    'Quarto Crescente': 'Antes de expandir, consolide o que já tem crescimento sustentável é o caminho.',
    'Quarto Minguante': 'Revise crenças e filosofias de vida o que você realmente acredita ser verdade?',
    'Lua Minguante':    'Revisão de valores profundos o que alimenta sua fé e senso de possibilidade?',
  },
  'Saturno': {
    'Lua Nova':         'Novo ciclo com antigas responsabilidades revise compromissos de longo prazo com honestidade.',
    'Lua Cheia':        'A Lua Cheia ilumina estruturas que precisam ser reformadas enfrente com maturidade.',
    'Quarto Crescente': 'Antes de novos compromissos, honre os existentes com integridade plena.',
    'Quarto Minguante': 'Dissolva estruturas rígidas que limitam seu crescimento genuíno.',
    'Lua Minguante':    'Recolhimento e revisão das bases o que ainda sustenta sua vida com solidez?',
  },
  'Urano': {
    'Lua Nova':         'Mudanças inesperadas podem surgir mantenha abertura em vez de resistência.',
    'Lua Cheia':        'A Lua Cheia pode trazer revelações surpreendentes acolha sem pânico ou rigidez.',
    'Quarto Crescente': 'Inovação requer base sólida não abandone o que funciona por mera novidade.',
    'Quarto Minguante': 'Libere padrões que limitam sua autenticidade e expressão mais genuína.',
    'Lua Minguante':    'Integre mudanças passadas antes de abraçar as próximas transformações.',
  },
  'Netuno': {
    'Lua Nova':         'Sonhos e visões em gestação registre intuições mas verifique antes de agir.',
    'Lua Cheia':        'A Lua Cheia intensifica ilusões distingua intuição genuína de projeção emocional.',
    'Quarto Crescente': 'Antes de se comprometer, confirme se a realidade corresponde à visão interna.',
    'Quarto Minguante': 'Dissolva ilusões com gentileza a verdade liberta mais do que a fantasia.',
    'Lua Minguante':    'Mergulho contemplativo deixe a névoa se dissolver naturalmente.',
  },
  'Plutão': {
    'Lua Nova':         'Transformações profundas em gestação o que precisa morrer para que algo novo renasça?',
    'Lua Crescente':    'Forças de mudança ganham impulso não resista ao que precisa se transformar.',
    'Quarto Crescente': 'Enfrente dinâmicas de poder com consciência quem controla o quê em sua vida?',
    'Gibosa Crescente': 'Antes da plenitude, o que precisa ser liberado para o verdadeiro florescimento?',
    'Lua Cheia':        'A Lua Cheia expõe sombras e padrões ocultos momento de integração e lucidez.',
    'Gibosa Minguante': 'Compartilhe a sabedoria das suas transformações há poder profundo no que você viveu.',
    'Quarto Minguante': 'Libere estruturas de poder que não servem mais à sua evolução pessoal.',
    'Lua Minguante':    'Recolhimento profundo integre as transformações antes do próximo ciclo.',
  },
};

// Per-planet × moon-sign notes (appended to the moon-phase message for the most resonant combinations)
const RETROGRADE_SIGN_NOTES: Partial<Record<string, Partial<Record<string, string>>>> = {
  'Mercúrio': {
    'Áries':       'Com a Lua em Áries, cuidado com respostas impulsivas pense duas vezes antes de enviar.',
    'Touro':       'Com a Lua em Touro, a resistência a mudanças nos planos pode ser um obstáculo flexibilize.',
    'Gêmeos':      'Mercúrio rege Gêmeos: com a Lua aqui, a confusão comunicativa está no ápice. Revise tudo com dobro de atenção.',
    'Câncer':      'Com a Lua em Câncer, mal-entendidos emocionais e familiares são mais frequentes escolha as palavras com cuidado.',
    'Leão':        'Com a Lua em Leão, o ego pode bloquear o diálogo verifique se o orgulho não está impedindo a escuta.',
    'Virgem':      'Mercúrio rege Virgem: com a Lua aqui, análise em excesso e crítica exagerada são as armadilhas do dia.',
    'Libra':       'Com a Lua em Libra, a indecisão em contratos e acordos fica amplificada espere clareza antes de assinar.',
    'Escorpião':   'Com a Lua em Escorpião, informações ocultas podem vir à tona boa hora para investigar, má para esconder.',
    'Sagitário':   'Com a Lua em Sagitário, cuidado com promessas excessivas e planos de viagem sem confirmação.',
    'Capricórnio': 'Com a Lua em Capricórnio, revise e-mails e comunicações de trabalho com atenção redobrada.',
    'Aquário':     'Com a Lua em Aquário, falhas tecnológicas estão amplificadas faça backup e evite atualizações de sistema.',
    'Peixes':      'Com a Lua em Peixes, confusão e ilusão estão no pico verifique duas vezes cada informação recebida.',
  },
  'Vênus': {
    'Touro':     'Com a Lua em Touro (regência de Vênus), compras impulsivas e apego financeiro ficam exacerbados.',
    'Libra':     'Com a Lua em Libra (regência de Vênus), relacionamentos e acordos pedem revisão cuidadosa.',
    'Escorpião': 'Com a Lua em Escorpião, sentimentos profundos e ciúmes podem aflorar abordagem com gentileza.',
    'Áries':     'Com a Lua em Áries, declarações afetivas impulsivas podem gerar arrependimentos aguarde.',
  },
  'Marte': {
    'Áries':     'Com a Lua em Áries (regência de Marte), impulsividade e conflitos estão no ápice respire antes de reagir.',
    'Escorpião': 'Com a Lua em Escorpião (corregência), intensidade emocional e confrontos podem escalar rapidamente.',
    'Libra':     'Com a Lua em Libra, a tensão entre ação e diplomacia é máxima escolha o diálogo.',
  },
  'Júpiter': {
    'Sagitário': 'Com a Lua em Sagitário (regência de Júpiter), otimismo excessivo pode obscurecer riscos reais.',
    'Peixes':    'Com a Lua em Peixes (corregência), expectativas infladas e ilusões financeiras estão em alta.',
  },
  'Saturno': {
    'Capricórnio': 'Com a Lua em Capricórnio (regência de Saturno), rigidez e pressão por resultados são armadilhas recalibrar.',
    'Aquário':     'Com a Lua em Aquário, tensão entre liberdade e estrutura pede equilíbrio consciente.',
  },
  'Urano': {
    'Aquário':  'Com a Lua em Aquário (regência de Urano), mudanças repentinas em sistemas e tecnologia são prováveis.',
    'Touro':    'Com a Lua em Touro, instabilidade financeira e material inesperada pode surgir mantenha reservas.',
  },
  'Netuno': {
    'Peixes':   'Com a Lua em Peixes (regência de Netuno), intuição e ilusão estão no mesmo nível máximo discernimento é essencial.',
    'Câncer':   'Com a Lua em Câncer, confusão emocional e instinto exagerado pedem aterramento na realidade.',
    'Escorpião':'Com a Lua em Escorpião, sonhos intensos e intuições poderosas merecem atenção mas confirme antes de agir.',
  },
  'Plutão': {
    'Escorpião':   'Com a Lua em Escorpião (regência de Plutão), transformações profundas e confrontos com sombras internas estão no ápice.',
    'Capricórnio': 'Com a Lua em Capricórnio, dinâmicas de poder no trabalho e nas estruturas sociais são foco de transformação.',
    'Áries':       'Com a Lua em Áries, conflitos de poder e crises de identidade pedem resolução corajosa e consciente.',
  },
};

function getRetrogradeDayImpact(
  planet: string,
  moon: typeof MOON_PHASES[number],
  moonSign: typeof MOON_SIGNS[number],
  avgScore: number,
): string {
  const moonSpecific = RETROGRADE_MOON_CONTEXT[planet]?.[moon.name];
  const base = moonSpecific ?? (PLANET_IMPACTS[planet] ?? 'Revise decisões e evite ações precipitadas nesta área.');
  let result = base;
  if (!moonSpecific) {
    if (avgScore >= 8) result += ' A energia favorável do dia atenua o efeito avance com consciência.';
    else if (avgScore <= 3) result += ' A energia baixa amplifica o impacto retrógrado priorize revisão e descanso.';
  }
  const signNote = RETROGRADE_SIGN_NOTES[planet]?.[moonSign.name];
  if (signNote) result += ` ${signNote}`;
  return result;
}

// ─────────────────────────────────────────
// Daily forecast built from moon + scores + insight tip
// ─────────────────────────────────────────

const MOON_INTROS: Record<string, string> = {
  'Lua Nova':         'A Lua Nova marca o início de um ciclo intenções semeadas hoje ganham força gradualmente.',
  'Lua Crescente':    'A Lua Crescente expande a energia impulso favorável para iniciativas e novos contatos.',
  'Quarto Crescente': 'Com o Quarto Crescente, a ação é chamada supere obstáculos com foco e determinação.',
  'Gibosa Crescente': 'Lua Gibosa Crescente: refinamento em curso persevere e ajuste seus projetos.',
  'Lua Cheia':        'A Lua Cheia ilumina o que floresceu celebre conquistas e libere o que não serve mais.',
  'Gibosa Minguante': 'Lua Gibosa Minguante: hora de compartilhar distribua o aprendizado acumulado.',
  'Quarto Minguante': 'Quarto Minguante: momento de soltar perdoe, libere e abra espaço para o novo.',
  'Lua Minguante':    'Lua Balsâmica pede recolhimento descanse, integre e prepare-se para renascer.',
};

function generateDayForecast(
  dateStr: string,
  data: CalendarData,
  retroPlanets: string[],
  moon: typeof MOON_PHASES[number],
  moonSign: typeof MOON_SIGNS[number],
): string {
  const insight   = data.day_insights?.[dateStr];
  const avgScore  = (
    (data.love_days[dateStr]       ?? 5) +
    (data.work_days[dateStr]       ?? 5) +
    (data.health_days[dateStr]     ?? 5) +
    (data.creativity_days[dateStr] ?? 5)
  ) / 4;
  const isBestL = data.best_days_love?.includes(dateStr);
  const isBestW = data.best_days_work?.includes(dateStr);
  const isBestH = data.best_days_health?.includes(dateStr);
  const isAvoid = data.avoid_days?.includes(dateStr);

  const phaseIntro = MOON_INTROS[moon.name] ?? moon.influence.split('.')[0] + '.';
  const moonIntro = `${phaseIntro} Com a Lua em ${moonSign.name} (${moonSign.element}), ${moonSign.keywords} está em evidência.`;

  let energyLine: string;
  if (isAvoid) {
    energyLine = avgScore <= 3
      ? 'As energias pedem cautela máxima prefira reflexão a decisões importantes.'
      : 'Tensão energética presente avance com atenção e evite confrontos diretos.';
  } else if (isBestL) {
    energyLine = avgScore >= 7
      ? 'O coração floresce com facilidade excelente para conexões afetivas e expressão emocional.'
      : 'Energia relacional favorável cultive vínculos com presença e escuta genuína.';
  } else if (isBestW) {
    energyLine = avgScore >= 7
      ? 'Clareza e foco profissional em alta ideal para entregas e decisões importantes.'
      : 'Produtividade constante tarefas práticas trazem resultados concretos.';
  } else if (isBestH) {
    energyLine = 'Vitalidade em expansão cuide do corpo e celebre o bem-estar físico e emocional.';
  } else if (avgScore >= 8) {
    energyLine = 'Energia geral elevada dia propício para avançar com confiança em várias frentes.';
  } else if (avgScore >= 5) {
    energyLine = 'Ritmo equilibrado bom para manutenção de projetos, comunicação e pequenos avanços.';
  } else {
    energyLine = 'Dia mais introspectivo respeite o ritmo natural e priorize autocuidado e descanso.';
  }

  const mainText = insight?.tip ?? energyLine;

  let retroNote = '';
  if (retroPlanets.length === 1) {
    retroNote = ` Atenção ao período retrógrado de ${PLANET_GLYPHS[retroPlanets[0]] ?? ''}${retroPlanets[0]}.`;
  } else if (retroPlanets.length > 1) {
    retroNote = ` Período com ${retroPlanets.length} planetas retrógrados ativos veja impactos abaixo.`;
  }

  return `${moonIntro} ${mainText}${retroNote}`;
}

// ─────────────────────────────────────────
// Day cell
// ─────────────────────────────────────────

function DayCell({
  dateStr, data, area, isToday, onSelect, retroEvents,
}: {
  dateStr: string;
  data: CalendarData;
  area: AreaTab;
  isToday: boolean;
  onSelect: (d: string) => void;
  retroEvents: { planet: string; starts_at: string; ends_at: string }[];
}) {
  const day        = new Date(dateStr + 'T12:00:00').getDate();
  const score      = areaScore(data, dateStr, area);
  const insight    = data.day_insights?.[dateStr];
  const isAvoid    = data.avoid_days?.includes(dateStr);
  const isBestL    = data.best_days_love?.includes(dateStr);
  const isBestW    = data.best_days_work?.includes(dateStr);
  const isEclipse  = data.eclipse_dates?.includes(dateStr);
  // Active retrograde planets for this day used for badge and aria-label
  const activePlanets = retroEvents.length > 0
    ? [...new Set(
        retroEvents
          .filter(ev => ev.starts_at <= dateStr && dateStr <= ev.ends_at)
          .map(ev => ev.planet),
      )]
    : Object.entries(data.retrograde_periods ?? {})
        .filter(([, dates]) => dates.length >= 2 && dates[0] <= dateStr && dateStr <= (dates[1] ?? '9999-12-31'))
        .map(([planet]) => planet);
  const moon       = getMoonPhase(dateStr);

  return (
    <button
      onClick={() => onSelect(dateStr)}
      style={{
        position:      'relative',
        background:    isToday ? 'rgba(31,78,95,0.18)' : scoreBg(score),
        border:        isToday ? '1.5px solid var(--petrol)' : '1px solid rgba(255,255,255,0.07)',
        borderRadius:  'var(--radius-md)',
        padding:       '0.3rem 0.2rem 1rem',
        cursor:        'pointer',
        textAlign:     'center',
        minHeight:     '4.5rem',
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'center',
        gap:           '0.15rem',
        width:         '100%',
        transition:    'transform 0.1s',
        userSelect:    'none',
        overflow:      'hidden',
      }}
      aria-label={`${dateStr}, ${moon.name}, score ${score}${activePlanets.length > 0 ? `, retrógrado: ${activePlanets.join(', ')}` : ''}`}
    >
      {/* Top row: day number + moon phase emoji */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        width: '100%', padding: '0 2px',
      }}>
        <span style={{
          fontSize: '0.82rem', fontWeight: isToday ? 700 : 400,
          color: isToday ? 'var(--petrol)' : 'var(--text-primary)', lineHeight: 1.2,
        }}>
          {day}
        </span>
        <span style={{ fontSize: '0.72rem', lineHeight: 1.2 }} title={moon.name}>
          {moon.emoji}
        </span>
      </div>

      {/* Score bar */}
      <div style={{
        width: '82%', height: '3px', borderRadius: '2px',
        background: 'rgba(255,255,255,0.1)', overflow: 'hidden',
      }}>
        <div style={{
          width: isAvoid ? '100%' : `${(score / 10) * 100}%`,
          height: '100%',
          background: isAvoid ? '#ef4444' : scoreColor(score),
          borderRadius: '2px',
        }} />
      </div>

      {/* Score number */}
      <span style={{ fontSize: '0.68rem', fontWeight: 600, color: isAvoid ? '#ef4444' : scoreColor(score) }}>
        {score}
      </span>

      {/* Center badges: best-day and caution icons */}
      <div style={{ display: 'flex', gap: '1px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {isAvoid   && <span style={{ fontSize: '0.8rem' }} title="Dia desafiador">⚠️</span>}
        {isBestL && area !== 'work'  && <span style={{ fontSize: '0.8rem' }} title="Melhor dia para amor">💕</span>}
        {isBestW && area !== 'love'  && <span style={{ fontSize: '0.8rem' }} title="Melhor dia para trabalho">💼</span>}
        {insight && !isBestL && !isBestW && !isAvoid && (
          <span style={{ fontSize: '0.75rem', color: 'var(--petrol)' }} title={insight.title}>✦</span>
        )}
      </div>

      {/* Bottom: retrograde strip ℞ + planet glyphs (☿♇ etc.) */}
      {activePlanets.length > 0 && (
        <div style={{
          position: 'absolute', bottom: '2px', left: '2px',
          display: 'flex', alignItems: 'center', gap: '1px',
          background: 'rgba(192,132,252,0.18)',
          borderRadius: '3px', padding: '1px 3px',
        }} title={`Retrógrado: ${activePlanets.join(', ')}`}>
          <span style={{ fontSize: '0.6rem', color: '#c084fc', fontWeight: 900, lineHeight: 1 }}>℞</span>
          {activePlanets.map(p => (
            <span key={p} style={{ fontSize: '0.6rem', color: '#c084fc', fontWeight: 600, lineHeight: 1 }}>
              {PLANET_GLYPHS[p] ?? ''}
            </span>
          ))}
        </div>
      )}

      {/* Bottom-right: eclipse badge */}
      {isEclipse && (
        <div style={{
          position: 'absolute', bottom: '3px', right: '3px',
          background: 'rgba(109,40,217,0.88)', borderRadius: '50%',
          width: '13px', height: '13px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} title="Eclipse">
          <span style={{ fontSize: '0.55rem', lineHeight: 1 }}>🌑</span>
        </div>
      )}
    </button>
  );
}

// ─────────────────────────────────────────
// Day detail sheet (bottom sheet)
// ─────────────────────────────────────────

function DaySheet({
  dateStr, data, onClose, isPaid, todayStr, retroEvents,
}: {
  dateStr: string;
  data: CalendarData;
  onClose: () => void;
  isPaid: boolean;
  todayStr: string;
  retroEvents: { planet: string; starts_at: string; ends_at: string }[];
}) {
  const navigate    = useNavigate();
  const d           = new Date(dateStr + 'T12:00:00');
  const label       = `${d.getDate()} de ${PT_MONTHS[d.getMonth()]}`;
  const insight     = data.day_insights?.[dateStr];
  const canViewFull = isPaid || dateStr === todayStr;

  const scores = [
    { label: 'Amor',        emoji: '💕', val: data.love_days[dateStr]       ?? 5 },
    { label: 'Trabalho',    emoji: '💼', val: data.work_days[dateStr]       ?? 5 },
    { label: 'Saúde',       emoji: '🌿', val: data.health_days[dateStr]     ?? 5 },
    { label: 'Criatividade', emoji: '✨', val: data.creativity_days[dateStr] ?? 5 },
  ];

  const isBestL    = data.best_days_love?.includes(dateStr);
  const isBestW    = data.best_days_work?.includes(dateStr);
  const isAvoid    = data.avoid_days?.includes(dateStr);
  const isNew      = data.new_moon_date === dateStr;
  const isFull     = data.full_moon_date === dateStr;
  const isEclipse  = data.eclipse_dates?.includes(dateStr);

  // Retrograde: collect planet names whose period spans this day
  // Use authoritative DB events when available, fall back to calendar's retrograde_periods
  const retroPlanets = retroEvents.length > 0
    ? [...new Set(
        retroEvents
          .filter(ev => ev.starts_at <= dateStr && dateStr <= ev.ends_at)
          .map(ev => ev.planet),
      )]
    : Object.entries(data.retrograde_periods ?? {})
        .filter(([, dates]) => dates.length >= 2 && dates[0] <= dateStr && dateStr <= (dates[1] ?? '9999-12-31'))
        .map(([planet]) => planet);

  const moon     = getMoonPhase(dateStr);
  const moonSign = getMoonSign(dateStr);
  const avgScore = (
    (data.love_days[dateStr]       ?? 5) +
    (data.work_days[dateStr]       ?? 5) +
    (data.health_days[dateStr]     ?? 5) +
    (data.creativity_days[dateStr] ?? 5)
  ) / 4;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} />

      {/* Sheet */}
      <div
        style={{
          position:   'relative',
          background: 'var(--surface-2, #1a1225)',
          borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
          padding: '1.5rem 1.25rem 2rem',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle */}
        <div style={{ width: '40px', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', margin: '0 auto 1rem' }} />

        {/* Date label */}
        <h3 className="font-display" style={{ color: 'var(--petrol)', fontSize: '1.25rem', marginBottom: '0.25rem' }}>
          {label}
        </h3>

        {/* Gated content for free users viewing a non-today date */}
        {!canViewFull ? (
          <div style={{
            textAlign: 'center', padding: '1.5rem 0.5rem',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
          }}>
            <Lock size={28} style={{ color: 'var(--petrol)' }} />
            <p className="font-display" style={{ color: 'var(--petrol)', fontSize: '1rem', margin: 0 }}>
              Apenas hoje está disponível gratuitamente
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
              Assine para desbloquear detalhes completos de todos os dias do mês, incluindo pontuações por área, insights e análise astrológica.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="btn-primary"
              style={{ width: '100%', maxWidth: '16rem' }}
            >
              Ver planos
            </button>
            <button onClick={onClose} className="btn-ghost" style={{ width: '100%', maxWidth: '16rem' }}>
              Fechar
            </button>
          </div>
        ) : (
          <>
            {/* Quick-status badges */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {isBestL  && <span className="chip">💕 Ótimo para amor</span>}
              {isBestW  && <span className="chip">💼 Ótimo para trabalho</span>}
              {isAvoid  && <span className="chip" style={{ color: '#f87171' }}>⚠️ Dia desafiador</span>}
            </div>

            {/* ── Daily forecast ───────────────────────────────── */}
            <div style={{
              background: 'rgba(31,78,95,0.07)', border: '1px solid rgba(31,78,95,0.18)',
              borderRadius: 'var(--radius-md)', padding: '0.875rem', marginBottom: '1rem',
            }}>
              <p style={{ color: 'var(--petrol)', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                ✦ Previsão do Dia
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0, lineHeight: 1.65 }}>
                {generateDayForecast(dateStr, data, retroPlanets, moon, moonSign)}
              </p>
            </div>

            {/* ── Moon phase section ───────────────────────────── */}
            {(() => {
              const isSpecialMoon = isNew || isFull;
              return (
                <div style={{
                  background: isSpecialMoon ? 'rgba(15,15,40,0.5)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${isSpecialMoon ? 'rgba(120,100,200,0.35)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: 'var(--radius-md)', padding: '0.875rem', marginBottom: '1rem',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
                    <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>{moon.emoji}</span>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--petrol)', display: 'block' }}>
                        {moon.name}
                      </span>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', letterSpacing: '0.03em' }}>
                        {moonSign.symbol} em {moonSign.name} · {moonSign.element}
                      </span>
                    </div>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0, lineHeight: 1.6 }}>
                    {moon.influence}
                  </p>
                </div>
              );
            })()}

            {/* ── Day insight ──────────────────────────────────── */}
            {insight && (
              <div style={{
                background: 'rgba(31,78,95,0.08)', border: '1px solid rgba(31,78,95,0.2)',
                borderRadius: 'var(--radius-md)', padding: '0.875rem', marginBottom: '1rem',
              }}>
                <p style={{ color: 'var(--petrol)', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                  ✦ {insight.title}
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: 0, lineHeight: 1.65 }}>
                  {insight.tip}
                </p>
              </div>
            )}

            {/* ── Retrograde section (per planet) ─────────────── */}
            {retroPlanets.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#c084fc', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                  ℞ {retroPlanets.map(p => `${PLANET_GLYPHS[p] ?? ''}${p}`).join(' · ')} Retrógrado{retroPlanets.length > 1 ? 's' : ''}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {retroPlanets.map(planet => (
                    <div key={planet} style={{
                      background: 'rgba(192,132,252,0.07)', border: '1px solid rgba(192,132,252,0.2)',
                      borderLeft: '3px solid rgba(192,132,252,0.6)',
                      borderRadius: 'var(--radius-md)', padding: '0.6rem 0.75rem',
                    }}>
                      <p style={{ color: '#c084fc', fontWeight: 600, fontSize: '0.85rem', margin: '0 0 0.2rem' }}>
                        {PLANET_GLYPHS[planet] ?? ''} {planet} ℞
                      </p>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0, lineHeight: 1.5 }}>
                        {getRetrogradeDayImpact(planet, moon, moonSign, avgScore)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Eclipse section ──────────────────────────────── */}
            {isEclipse && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(88,28,135,0.2), rgba(30,27,75,0.25))',
                border: '1px solid rgba(139,92,246,0.4)',
                borderRadius: 'var(--radius-md)', padding: '0.875rem', marginBottom: '1rem',
              }}>
                <p style={{ color: '#a78bfa', fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                  🌑 Eclipse, Portal de Transformação
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0, lineHeight: 1.6 }}>
                  Eclipses aceleram transformações que já estavam em andamento. Fique atento a revelações, encerramentos e mudanças de direção. O que surgir hoje tende a ter impacto duradouro.
                </p>
              </div>
            )}

            {/* Area scores */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
              {scores.map(({ label: l, emoji, val }) => (
                <div key={l} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ width: '1.25rem', textAlign: 'center', fontSize: '0.9rem' }}>{emoji}</span>
                  <span style={{ width: '5.5rem', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{l}</span>
                  <div style={{ flex: 1, height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px' }}>
                    <div style={{ width: `${(val / 10) * 100}%`, height: '100%', background: scoreColor(val), borderRadius: '3px', transition: 'width 0.4s' }} />
                  </div>
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: scoreColor(val), minWidth: '1.5rem', textAlign: 'right' }}>{val}</span>
                </div>
              ))}
            </div>

            <button
              onClick={onClose}
              className="btn-ghost"
              style={{ width: '100%', marginTop: '0.5rem' }}
            >
              Fechar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Local moon phase computation (synodic month)
// Reference new moon: 2000-01-06T18:14:00Z = JD 2451550.259
// ─────────────────────────────────────────

function computeMoonPhaseDates(year: number, month: number): { newMoon: Date | null; fullMoon: Date | null } {
  const REF_JD  = 2451550.259; // JD of known new moon (2000-01-06)
  const SYNODIC = 29.53058867;

  function jdToDate(jd: number): Date {
    const z = Math.floor(jd + 0.5);
    const f = jd + 0.5 - z;
    let a = z;
    if (z >= 2299161) {
      const alpha = Math.floor((z - 1867216.25) / 36524.25);
      a = z + 1 + alpha - Math.floor(alpha / 4);
    }
    const b = a + 1524;
    const c = Math.floor((b - 122.1) / 365.25);
    const d = Math.floor(365.25 * c);
    const e = Math.floor((b - d) / 30.6001);
    const day   = b - d - Math.floor(30.6001 * e) + f;
    const mo    = e < 14 ? e - 1 : e - 13;
    const yr    = mo > 2 ? c - 4716 : c - 4715;
    return new Date(Date.UTC(yr, mo - 1, Math.floor(day)));
  }

  function dateToJD(y: number, m: number, day: number): number {
    const a = Math.floor((14 - m) / 12);
    const yr = y + 4800 - a;
    const mo = m + 12 * a - 3;
    return day + Math.floor((153 * mo + 2) / 5) + 365 * yr +
      Math.floor(yr / 4) - Math.floor(yr / 100) + Math.floor(yr / 400) - 32045;
  }

  const monthStart = dateToJD(year, month, 1);
  const monthEnd   = dateToJD(year, month === 12 ? year + 1 : year, month === 12 ? 1 : month + 1) - 1;

  // Find nearest new moon before or at month start
  const kStart = Math.floor((monthStart - REF_JD) / SYNODIC);

  let newMoon: Date | null = null;
  let fullMoon: Date | null = null;

  for (let k = kStart - 1; k <= kStart + 2; k++) {
    const nmJD  = REF_JD + k * SYNODIC;
    const fmJD  = REF_JD + k * SYNODIC + SYNODIC / 2;
    if (!newMoon  && nmJD >= monthStart && nmJD <= monthEnd) newMoon  = jdToDate(nmJD);
    if (!fullMoon && fmJD >= monthStart && fmJD <= monthEnd) fullMoon = jdToDate(fmJD);
  }

  return { newMoon, fullMoon };
}

// ─────────────────────────────────────────
// Lunar phases card section
// ─────────────────────────────────────────

function LunarPhasesSection({ data, year, month }: { data: CalendarData | null; year: number; month: number }) {
  const computed = computeMoonPhaseDates(year, month);

  const parseDate = (ds: string | null | undefined): Date | null =>
    ds ? new Date(ds + 'T12:00:00') : null;

  const newMoonDate  = parseDate(data?.new_moon_date)  ?? computed.newMoon;
  const fullMoonDate = parseDate(data?.full_moon_date) ?? computed.fullMoon;
  const eclipseDates = (data?.eclipse_dates ?? []).map(ds => new Date(ds + 'T12:00:00'));

  const fmt = (d: Date) => d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });

  const phases = [
    newMoonDate  && { emoji: '🌑', label: 'Lua Nova',   date: fmt(newMoonDate),  bg: 'rgba(15,15,30,0.4)',  border: 'rgba(100,80,180,0.3)', tip: 'Momento de intenções, novos começos e plantio.' },
    fullMoonDate && { emoji: '🌕', label: 'Lua Cheia',  date: fmt(fullMoonDate), bg: 'rgba(245,200,66,0.08)', border: 'rgba(245,200,66,0.3)', tip: 'Momento de colheita, iluminação e liberação.' },
    ...eclipseDates.map(d => ({ emoji: '🌒', label: 'Eclipse', date: fmt(d), bg: 'rgba(180,40,40,0.08)', border: 'rgba(200,80,80,0.3)', tip: 'Período de transformações aceleradas.' })),
  ].filter(Boolean) as { emoji: string; label: string; date: string; bg: string; border: string; tip: string }[];

  if (!phases.length) return null;

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <p style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '0 0 0.625rem' }}>
        🌙 Fases da Lua, {PT_MONTHS[month - 1]}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(phases.length, 3)}, 1fr)`, gap: '0.5rem' }}>
        {phases.map(ph => (
          <div key={ph.label + ph.date} style={{
            background: ph.bg,
            border: `1px solid ${ph.border}`,
            borderRadius: 'var(--radius-md)',
            padding: '0.875rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem' }}>
              <span style={{ fontSize: '1.25rem' }}>{ph.emoji}</span>
              <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{ph.label}</span>
            </div>
            <p style={{ margin: '0 0 0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--petrol)' }}>{ph.date}</p>
            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.45 }}>{ph.tip}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Special days section
// ─────────────────────────────────────────

function SpecialDays({ data }: { data: CalendarData }) {
  const fmt = (ds: string) => {
    const d = new Date(ds + 'T12:00:00');
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
      {data.best_days_love?.length > 0 && (
        <div className="card" style={{ padding: '0.875rem' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: '0.35rem' }}>
            💕 Melhores dias para amor
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0 }}>
            {data.best_days_love.map(fmt).join(' · ')}
          </p>
        </div>
      )}
      {data.best_days_work?.length > 0 && (
        <div className="card" style={{ padding: '0.875rem' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: '0.35rem' }}>
            💼 Dias favoráveis para decisões profissionais
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0 }}>
            {data.best_days_work.map(fmt).join(' · ')}
          </p>
        </div>
      )}
      {data.best_days_health?.length > 0 && (
        <div className="card" style={{ padding: '0.875rem' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: '0.35rem' }}>
            🌿 Melhores dias para saúde e bem-estar
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0 }}>
            {data.best_days_health.map(fmt).join(' · ')}
          </p>
        </div>
      )}
      {data.avoid_days?.length > 0 && (
        <div className="card" style={{ padding: '0.875rem', borderColor: 'rgba(248,113,113,0.2)' }}>
          <p style={{ fontWeight: 600, fontSize: '0.85rem', color: '#f87171', marginBottom: '0.35rem' }}>
            ⚠️ Dias para cautela
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0 }}>
            {data.avoid_days.map(fmt).join(' · ')}
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// Push notification reminder toggle
// ─────────────────────────────────────────

function NotificationToggle({ userId, isPaid }: { userId: string; isPaid: boolean }) {
  const navigate = useNavigate();
  const [enabled,       setEnabled]       = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [channel,       setChannel]       = useState<'email' | 'whatsapp'>('email');
  const [whatsappNum,   setWhatsappNum]   = useState('');

  useEffect(() => {
    supabase
      .from('platform_settings')
      .select('key, value')
      .in('key', [`agenda_notif_${userId}`, `notif_channel_${userId}`, `notif_whatsapp_${userId}`])
      .then(({ data }) => {
        for (const row of (data ?? []) as { key: string; value: unknown }[]) {
          if (row.key === `agenda_notif_${userId}` && (row.value === 'true' || row.value === true)) setEnabled(true);
          if (row.key === `notif_channel_${userId}` && (row.value === 'email' || row.value === 'whatsapp')) setChannel(row.value as 'email' | 'whatsapp');
          if (row.key === `notif_whatsapp_${userId}` && typeof row.value === 'string') setWhatsappNum(row.value);
        }
      });
  }, [userId]);

  const toggle = useCallback(async () => {
    if (!isPaid) { navigate('/pricing'); return; }
    setSaving(true);
    const next = !enabled;
    await supabase.from('platform_settings').upsert({
      key:         `agenda_notif_${userId}`,
      value:       String(next),
      description: `Agenda notifications for user ${userId}`,
      updated_at:  new Date().toISOString(),
    });
    setEnabled(next);
    setSaving(false);
  }, [enabled, userId, isPaid, navigate]);

  const channelLabel = channel === 'whatsapp' && whatsappNum
    ? `WhatsApp (${whatsappNum})`
    : channel === 'whatsapp'
    ? 'WhatsApp'
    : 'E-mail';

  return (
    <div
      className="card"
      style={{
        padding: '0.875rem 1rem', marginTop: '1rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {enabled ? (
            <Bell size={16} style={{ color: 'var(--petrol)' }} />
          ) : (
            <BellOff size={16} style={{ color: 'var(--text-muted)' }} />
          )}
          <div>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Lembrar nos dias especiais
            </span>
            {!isPaid && (
              <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', background: 'rgba(245,200,66,0.15)', color: 'var(--petrol)', borderRadius: '999px', padding: '1px 6px', fontWeight: 700 }}>
                Premium
              </span>
            )}
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={saving}
          style={{
            width: '42px', height: '24px', borderRadius: '12px',
            background: enabled && isPaid ? 'var(--petrol)' : 'rgba(255,255,255,0.15)',
            border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
            flexShrink: 0,
          }}
          aria-label={enabled ? 'Desativar lembretes' : 'Ativar lembretes'}
        >
          <span style={{
            position: 'absolute', top: '3px',
            left: enabled && isPaid ? '21px' : '3px',
            width: '18px', height: '18px',
            borderRadius: '50%', background: '#fff',
            transition: 'left 0.2s',
          }} />
        </button>
      </div>

      {enabled && isPaid && (
        <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          Enviaremos via{' '}
          <span style={{ color: 'var(--petrol)', fontWeight: 600 }}>{channelLabel}</span>
          {channel === 'whatsapp' && !whatsappNum && (
            <>
              {', '}
              <button
                onClick={() => navigate('/configuracoes')}
                style={{ background: 'none', border: 'none', color: 'var(--petrol)', cursor: 'pointer', fontSize: '0.75rem', padding: 0, textDecoration: 'underline' }}
              >
                configurar número
              </button>
            </>
          )}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// Calendar loading state with rotating messages
// ─────────────────────────────────────────

const LOADING_MESSAGES = [
  'Consultando os astros...',
  'Analisando posições planetárias...',
  'Calculando trânsitos do mês...',
  'Mapeando fases lunares...',
  'Identificando dias favoráveis...',
  'Verificando planetas retrógrados...',
  'Preparando seu calendário personalizado...',
  'Alinhando energias cósmicas...',
];

function CalendarLoadingState() {
  const [msgIdx, setMsgIdx] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setMsgIdx(i => (i + 1) % LOADING_MESSAGES.length);
    }, 2500);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div style={{
      padding: '3rem 1rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1rem',
    }}>
      <div style={{
        width: '2.5rem', height: '2.5rem',
        border: '2px solid rgba(31,78,95,0.3)',
        borderTopColor: 'var(--petrol)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <p style={{
        color: 'var(--text-secondary)',
        fontSize: '0.85rem',
        textAlign: 'center',
        minHeight: '1.4em',
        transition: 'opacity 0.3s',
      }}>
        {LOADING_MESSAGES[msgIdx]}
      </p>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center' }}>
        Isso pode levar até 1 minuto na primeira vez
      </p>
    </div>
  );
}

// ─────────────────────────────────────────
// Main page
// ─────────────────────────────────────────

export function AstrologicalAgenda() {
  useSystemUsageTracker('agenda_astrologica');
  const { profile } = useAuth();
  const navigate    = useNavigate();

  const isPaid = profile?.subscription_tier === 'monthly' || profile?.subscription_tier === 'annual' || profile?.unlimited_access === true;

  // Month navigation
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-based

  const currentMonthStr = `${year}-${String(month).padStart(2, '0')}`;

  // Calendar data per month (cached in state)
  const [calendarCache, setCalendarCache] = useState<Record<string, CalendarData>>({});
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [activeArea, setActiveArea]   = useState<AreaTab>('all');

  // Authoritative retrograde events fetched directly from DB
  const [retroEvents, setRetroEvents] = useState<{ planet: string; starts_at: string; ends_at: string }[]>([]);
  useEffect(() => {
    supabase
      .from('retrograde_events')
      .select('planet, starts_at, ends_at')
      .gte('year', today.getFullYear())
      .then(({ data: evs }) => {
        if (evs && evs.length > 0) {
          setRetroEvents(evs as { planet: string; starts_at: string; ends_at: string }[]);
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calendarData = calendarCache[currentMonthStr] ?? null;

  const fetchCalendar = useCallback(async (ym: string) => {
    if (!profile?.birth_date) return;
    if (calendarCache[ym]) return; // already loaded

    setLoading(true);
    setError(null);

    const result = await safeInvoke<CalendarData & { fromCache?: boolean }>(
      'generate-monthly-calendar',
      { body: { month: ym }, timeout: 90_000, fallback: null as unknown as CalendarData },
    );

    if (result && !result.fromFallback && result.data) {
      setCalendarCache(prev => ({ ...prev, [ym]: result.data as CalendarData }));
    } else {
      setError('Não foi possível gerar o calendário. Verifique seu mapa natal e tente novamente.');
    }
    setLoading(false);
  }, [profile?.birth_date, calendarCache]);

  useEffect(() => {
    fetchCalendar(currentMonthStr);
  }, [currentMonthStr, fetchCalendar]);

  // Build calendar grid days
  const gridDays = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay  = new Date(year, month, 0);
    const startPad = firstDay.getDay(); // 0=Sun
    const days: (string | null)[] = Array(startPad).fill(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(`${currentMonthStr}-${String(d).padStart(2, '0')}`);
    }
    return days;
  }, [year, month, currentMonthStr]);

  const todayStr = today.toISOString().slice(0, 10);

  const goBack = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };

  const goNext = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  if (!profile?.birth_date) {
    return (
      <div className="page-container">
        <EmptyState
          emoji="📅"
          title="Mapa natal necessário"
          subtitle="Para ver sua Agenda Astrológica, primeiro calcule seu Mapa Natal."
          cta="Calcular Mapa Natal"
          ctaAction={() => navigate('/birth-chart')}
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Page title */}
      <div style={{ marginBottom: '1.25rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.25rem,4vw,1.75rem)', color: 'var(--text-primary)', marginBottom: '0.2rem' }}>
          📅 Agenda Astrológica
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Calendário personalizado com seu mapa natal
        </p>
      </div>

      {/* Month navigation header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: isPaid ? '1rem' : '0.4rem',
      }}>
        <button
          onClick={isPaid ? goBack : () => navigate('/pricing')}
          className="btn-ghost"
          style={{ padding: '0.4rem', opacity: isPaid ? 1 : 0.45 }}
          aria-label="Mês anterior"
        >
          <ChevronLeft size={20} />
        </button>
        <h2 className="font-display" style={{ fontSize: '1.15rem', color: 'var(--petrol)', margin: 0 }}>
          {PT_MONTHS[month - 1]} {year}
        </h2>
        <button
          onClick={isPaid ? goNext : () => navigate('/pricing')}
          className="btn-ghost"
          style={{ padding: '0.4rem', opacity: isPaid ? 1 : 0.45 }}
          aria-label="Próximo mês"
        >
          <ChevronRight size={20} />
        </button>
      </div>
      {/* Free-user note under month nav */}
      {!isPaid && (
        <p style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          <Lock size={10} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />
          Navegação entre meses disponível apenas para assinantes.{' '}
          <button
            onClick={() => navigate('/pricing')}
            style={{ background: 'none', border: 'none', color: 'var(--petrol)', cursor: 'pointer', fontSize: '0.72rem', padding: 0, textDecoration: 'underline' }}
          >
            Ver planos
          </button>
        </p>
      )}

      {/* Monthly summary */}
      {calendarData?.monthly_summary && (
        <div style={{
          background: 'rgba(31,78,95,0.07)',
          border: '1px solid rgba(31,78,95,0.18)',
          borderRadius: 'var(--radius-md)',
          padding: '0.875rem',
          marginBottom: '1rem',
        }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.6, margin: 0 }}>
            ✦ {calendarData.monthly_summary}
          </p>
        </div>
      )}

      {/* Lunar phases section, shown always (uses local calc as fallback) */}
      <LunarPhasesSection data={calendarData} year={year} month={month} />

      {/* Area tabs */}
      <div style={{
        display: 'flex', gap: '0.35rem', overflowX: 'auto',
        paddingBottom: '0.5rem', marginBottom: '1rem',
        scrollbarWidth: 'none',
      }}>
        {AREA_TABS.map(({ key, label, emoji }) => (
          <button
            key={key}
            onClick={() => setActiveArea(key)}
            style={{
              padding:      '0.35rem 0.75rem',
              borderRadius: '999px',
              border:       activeArea === key ? '1.5px solid var(--petrol)' : '1px solid rgba(255,255,255,0.15)',
              background:   activeArea === key ? 'rgba(31,78,95,0.15)' : 'transparent',
              color:        activeArea === key ? 'var(--petrol)' : 'var(--text-secondary)',
              fontSize:     '0.8rem',
              fontWeight:   activeArea === key ? 600 : 400,
              cursor:       'pointer',
              whiteSpace:   'nowrap',
              flexShrink:   0,
            }}
          >
            {emoji} {label}
          </button>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ position: 'relative' }}>
        {/* Click hint */}
        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center', marginBottom: '0.5rem' }}>
          👆 Toque em qualquer data para ver os detalhes do dia · <span style={{ color: '#ef4444' }}>■</span> dias para cautela
        </p>

        {/* Week day headers */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '3px', marginBottom: '3px',
        }}>
          {PT_WEEK_DAYS.map(d => (
            <div key={d} style={{
              textAlign: 'center', fontSize: '0.7rem',
              color: 'var(--text-muted)', padding: '0.25rem 0',
            }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        {loading ? (
          <CalendarLoadingState />
        ) : error ? (
          <div style={{ padding: '1.5rem', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1rem' }}>{error}</p>
            <button onClick={() => fetchCalendar(currentMonthStr)} className="btn-ghost">
              Tentar novamente
            </button>
          </div>
        ) : calendarData ? (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '3px',
          }}>
            {gridDays.map((dateStr, idx) =>
              dateStr === null ? (
                <div key={`pad-${idx}`} />
              ) : (
                <DayCell
                  key={dateStr}
                  dateStr={dateStr}
                  data={calendarData}
                  area={activeArea}
                  isToday={dateStr === todayStr}
                  onSelect={setSelectedDay}
                  retroEvents={retroEvents}
                />
              ),
            )}
          </div>
        ) : null}
      </div>

      {/* Legend */}
      {!loading && calendarData && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}>
          {/* Score legend */}
          <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Pontuação
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            {[
              { label: '8–10 Favorável', color: '#4A7C59' },
              { label: '5–7 Neutro',     color: 'var(--petrol)' },
              { label: '1–4 Cauteloso',  color: '#ef4444' },
            ].map(({ label, color }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color }} />
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{label}</span>
              </div>
            ))}
          </div>

          {/* Icon legend */}
          <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Símbolos
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: '13px', height: '13px', borderRadius: '50%',
                background: 'rgba(192,132,252,0.88)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <span style={{ fontSize: '0.5rem', color: '#fff', fontWeight: 800, lineHeight: 1 }}>℞</span>
              </div>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Retrógrado</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: '13px', height: '13px', borderRadius: '50%',
                background: 'rgba(109,40,217,0.88)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <span style={{ fontSize: '0.55rem', lineHeight: 1 }}>🌑</span>
              </div>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Eclipse</span>
            </div>
            {[
              { emoji: '🌑', label: 'Lua Nova' },
              { emoji: '🌓', label: 'Quarto Crescente' },
              { emoji: '🌕', label: 'Lua Cheia' },
              { emoji: '🌗', label: 'Quarto Minguante' },
            ].map(({ emoji, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '0.82rem', lineHeight: 1 }}>{emoji}</span>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Special days section */}
      {calendarData && isPaid && <SpecialDays data={calendarData} />}

      {/* Notification reminder toggle, shown to all, gated inside for free users */}
      {profile && <NotificationToggle userId={profile.id} isPaid={isPaid} />}

      {/* Day detail bottom sheet */}
      {selectedDay && calendarData && (
        <DaySheet
          dateStr={selectedDay}
          data={calendarData}
          onClose={() => setSelectedDay(null)}
          isPaid={isPaid}
          todayStr={todayStr}
          retroEvents={retroEvents}
        />
      )}
    </div>
  );
}
